import React, { useContext, useEffect, useState, useRef } from 'react';
import { Box, Grid, Typography, IconButton, useTheme, useMediaQuery, Menu, MenuItem, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { useNavigate } from 'react-router-dom';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import DownloadUtils from '../../../../utils/download-utils';
import WhiteboardCard from './WhiteboardCard';
import { FeedbackCTA } from '../../feedback-cta';
import GradientBanner from '../../../UIControls/GradientBanner';
import PinkGradientButtonWithArrow from '../../../UIControls/PinkGradientButtonWithArrow';
import { archiveWhiteboard, cleanupPatientWhiteboardsFromLibrary } from '../../../helper/whiteboardUtils';

const MyLibrary = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { userDataStore, whiteboardStore, authStore, imageStore } = useContext(StoreContext);

    // State variables
    const [whiteboards, setWhiteboards] = useState([]);
    const [filteredWhiteboards, setFilteredWhiteboards] = useState([]);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [sortBy, setSortBy] = useState('RECENT');
    const [imageMap, setImageMap] = useState({});
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' = Newest first
    const [hasSortedRecently, setHasSortedRecently] = useState(false);
    const [favourites, setFavourites] = useState(userDataStore.userData.whiteboardsFavourites || []);

    const whiteboardsPerPage = 6;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();

    const paginatedWhiteboards = filteredWhiteboards.slice(page * whiteboardsPerPage, (page + 1) * whiteboardsPerPage);

    // **Prevent multiple fetches of images**
    const fetchedImages = useRef(new Set());

    // Cleanup patient whiteboards from My Library on mount
    useEffect(() => {
        const cleanup = async () => {
            if (!whiteboardStore.isInitialized) return;

            try {
                const result = await cleanupPatientWhiteboardsFromLibrary(userDataStore, whiteboardStore, authStore);
                if (result.cleaned) {
                    console.log(`Cleaned up ${result.count} patient whiteboards from My Library`);
                }
            } catch (err) {
                console.error('Failed to cleanup patient whiteboards:', err);
            }
        };

        cleanup();
    }, [whiteboardStore.isInitialized, userDataStore, whiteboardStore, authStore]);

    useEffect(() => {
        // Only update your local state after the store is ready
        if (!whiteboardStore.isInitialized) return;

        let whiteboardsData = Object.values(whiteboardStore.whiteboardData);
        console.log('Updating Whiteboards State');

        const myLibraryIds = userDataStore.userData.whiteboards || [];
        // Only show active records
        whiteboardsData = whiteboardsData
            .filter((wb) => wb.whiteboardStatus !== 'archived' && myLibraryIds.includes(wb.whiteboardId))
            .sort((a, b) => b.lastUpdated - a.lastUpdated);
        setWhiteboards(whiteboardsData);
        setFilteredWhiteboards(whiteboardsData);
    }, [whiteboardStore.whiteboardData, whiteboardStore.isInitialized]);

    useEffect(() => {
        setFavourites(userDataStore.userData.whiteboardsFavourites || []);
    }, [userDataStore.userData.whiteboardsFavourites]);

    useEffect(() => {
        if (whiteboards.length > 0) {
            setFilteredWhiteboards(whiteboards);
        }
    }, [whiteboards.length]);

    useEffect(() => {
        if (!whiteboardStore.isInitialized || paginatedWhiteboards.length === 0) return;

        let isMounted = true;
        const fetchImagesForWhiteboards = async () => {
            const newImageMap = {};
            let shouldUpdateState = false;

            for (const whiteboard of paginatedWhiteboards) {
                // console.log('Checking image for whiteboard:', whiteboard.whiteboardId, whiteboard.canvasImageLink);
                if (!whiteboard.canvasImageLink || fetchedImages.current.has(whiteboard.canvasImageLink)) {
                    // console.log(
                    //     'Skipping fetch for link:',
                    //     whiteboard.canvasImageLink,
                    //     'already in fetchedImages?',
                    //     fetchedImages.current.has(whiteboard.canvasImageLink),
                    // );
                    continue;
                }
                try {
                    const imageData = await handleFetchWhiteboardImage(whiteboard.canvasImageLink);
                    if (imageData && isMounted) {
                        newImageMap[whiteboard.canvasImageLink] = imageData;
                        fetchedImages.current.add(whiteboard.canvasImageLink);
                        shouldUpdateState = true; // Only update if new images were fetched
                    }
                } catch (error) {
                    console.error(`Error fetching whiteboard image: ${whiteboard.canvasImageLink}`, error);
                }
            }

            if (isMounted && shouldUpdateState) {
                setImageMap((prev) => ({ ...prev, ...newImageMap }));
                // console.warn('Updated Image Map:', newImageMap);
            }
        };

        fetchImagesForWhiteboards();
        return () => {
            isMounted = false;
        };
    }, [paginatedWhiteboards, whiteboardStore.isInitialized]);

    const handleSearchInputChange = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);

        const filtered = whiteboards.filter((wb) => {
            return (
                wb.handoutContentName?.toLowerCase().includes(value) ||
                wb.handoutAlphabeticalName?.toLowerCase().includes(value) ||
                wb.handoutDocumentTitle?.toLowerCase().includes(value) ||
                wb.notes?.toLowerCase().includes(value) ||
                wb.fileTitle?.toLowerCase().includes(value) ||
                wb.patientName?.toLowerCase().includes(value)
            );
        });

        setFilteredWhiteboards(filtered);
        setPage(0);
    };

    const handleNextPage = () => {
        if ((page + 1) * whiteboardsPerPage < filteredWhiteboards.length) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // close menu in sorting button
    const handleCloseMenu = (option) => {
        const favouriteIds = userDataStore.userData.whiteboardsFavourites || [];

        if (option === 'RECENT') {
            const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
            setSortDirection(newDirection);
            setHasSortedRecently(true);
            setSortBy('RECENT');

            const sorted = [...whiteboards].sort((a, b) =>
                newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
            );
            setFilteredWhiteboards(sorted);
            setPage(0);
        }

        if (option === 'FAVOURITES') {
            const isSameSort = sortBy === 'FAVOURITES';
            const newDirection = isSameSort && sortDirection === 'desc' ? 'asc' : 'desc';

            setSortDirection(newDirection);
            setHasSortedRecently(false);
            setSortBy('FAVOURITES');

            const favouritesGroup = whiteboards
                .filter((wb) => favouriteIds.includes(wb.whiteboardId))
                .sort((a, b) =>
                    newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
                );

            const nonFavouritesGroup = whiteboards
                .filter((wb) => !favouriteIds.includes(wb.whiteboardId))
                .sort((a, b) =>
                    newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
                );

            const sorted = [...favouritesGroup, ...nonFavouritesGroup];
            setFilteredWhiteboards(sorted);
            setPage(0);
        }

        setAnchorEl(null);
    };

    const handleFetchWhiteboardImage = async (canvasImageLink) => {
        if (!canvasImageLink) return null; // Prevent IndexedDB errors

        try {
            // Check if already loaded in state
            if (imageMap[canvasImageLink]) {
                return imageMap[canvasImageLink];
            }

            // Check if image exists in IndexedDB
            const imageObject = await imageStore.getImageFromIndexedDB(canvasImageLink);
            if (imageObject) return imageObject.data; // Return base64 image

            // Generate presigned URL
            const payload = {
                files: [{ method: 'get', key: canvasImageLink }],
                contentCategory: 'whiteboard',
            };
            const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
            const presignedUrl = resp.getUrls[0].presignedUrl;

            // Download Image
            const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

            // Store in IndexedDB
            await imageStore.saveImageToIndexedDB(canvasImageLink, downloadResult.base64data, downloadResult.eTag);

            return downloadResult.base64data;
        } catch (error) {
            console.error(`Error fetching whiteboard image ${canvasImageLink}:`, error);
            return null;
        }
    };

    const handleArchiveWhiteboard = (wb) =>
        archiveWhiteboard(
            wb,
            userDataStore,
            whiteboardStore,
            authStore,
            setWhiteboards,
            setFilteredWhiteboards,
            showSnackbar,
        );

    const handleToggleFavourite = async (whiteboardId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.whiteboardsFavourites?.includes(whiteboardId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.whiteboardsFavourites.filter((id) => id !== whiteboardId)
            : [...(userDataStore.userData.whiteboardsFavourites || []), whiteboardId];

        if (!isFavourite && userDataStore.userData.whiteboardsFavourites.length >= 30) {
            showSnackbar('You can only mark up to 30 whiteboards as favourites.', 'warning');
            return;
        }

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            whiteboardsFavourites: updatedFavourites,
        });

        // Send update to backend
        const payload = {
            whiteboardsFavourites: updatedFavourites,
        };

        setFavourites(updatedFavourites);

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update favourites', err);
        }
    };

    const handleEditWhiteboard = (whiteboard) => {
        if (!whiteboard.handoutId) {
            console.error('Missing handoutContentId for whiteboard:', whiteboard);
            return;
        }

        // Set whiteboard state in localStorage so it's loaded on Handout page
        localStorage.setItem(
            'lastWhiteboardState',
            JSON.stringify({
                whiteboardId: whiteboard.whiteboardId,
                handoutId: whiteboard.handoutId,
                canvasImageLink: whiteboard.canvasImageLink,
                canvasJsonLink: whiteboard.canvasJsonLink,
                fileName: whiteboard.fileName || '',
                eTag: whiteboard.eTag,
                ...(whiteboard.lastUsedColours && { lastUsedColours: whiteboard.lastUsedColours }),
                ...(whiteboard.updatedByFirstName && {
                    updatedByFirstName: whiteboard.updatedByFirstName,
                }),
                ...(whiteboard.updatedByLastName && {
                    updatedByLastName: whiteboard.updatedByLastName,
                }),
                ...(whiteboard.diagramVariant && { diagramVariant: whiteboard.diagramVariant }),
            }),
        );

        sessionStorage.setItem('autoloadWhiteboardFromLibrary', 'true');
        // pass details for disclaimer
        sessionStorage.setItem(
            'whiteboardVetName',
            `${whiteboard.updatedByFirstName || ''} ${whiteboard.updatedByLastName || ''}`.trim(),
        );
        sessionStorage.setItem('whiteboardClinicName', userDataStore?.userData?.clinicName || '');
        // Navigate to the handout page
        navigate(`/handout/${whiteboard.handoutId}`);
    };

    // **Prevent rendering until store is ready**
    if (!whiteboardStore.isInitialized) {
        return (
            <Typography variant="h6" align="center">
                Loading Whiteboards...
            </Typography>
        );
    }

    return (
        <>
            <GradientBanner
                title="My Library"
                subtitle="Explore, edit and share your saved whiteboards"
                placeholder="Search..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isTablet}
                height={isMobile ? 'initial' : '450px'}
                paddingY={isMobile ? '48px' : '40px'}
                paddingBottom="40px"
                className="search-banner"
            />

            {/* Main Panel */}
            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mt: 2,
                        pr: 2,
                        mb: '67px',
                        paddingX: isMobile ? '48px' : '66px',
                    }}
                >
                    {/* Sort By Text */}
                    <Typography
                        onClick={handleOpenMenu}
                        sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '14px',
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                            color: '#1919194D',
                            cursor: 'pointer',
                        }}
                    >
                        SORT BY{' '}
                        <Typography
                            component="span"
                            sx={{
                                color: '#000453',
                            }}
                        >
                            {sortBy}
                        </Typography>
                    </Typography>

                    {/* Dropdown Icon */}
                    <IconButton onClick={handleOpenMenu} sx={{ ml: 1, p: 0 }}>
                        {sortBy === 'RECENT' && hasSortedRecently ? (
                            sortDirection === 'desc' ? (
                                <ExpandMoreIcon sx={{ color: '#000453' }} />
                            ) : (
                                <ExpandLessIcon sx={{ color: '#000453' }} />
                            )
                        ) : (
                            <ExpandMoreIcon sx={{ color: '#000453' }} />
                        )}
                    </IconButton>

                    {/* Dropdown Menu */}
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleCloseMenu(null)}>
                        <MenuItem onClick={() => handleCloseMenu('RECENT')} sx={{ color: '#000453' }}>
                            RECENT
                        </MenuItem>
                        <MenuItem onClick={() => handleCloseMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                            FAVOURITES
                        </MenuItem>
                    </Menu>
                </Box>

                {filteredWhiteboards.length === 0 ? (
                    <Box sx={{ mt: 6, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#000453' }}>
                            No whiteboards available yet.
                        </Typography>

                        <Typography variant="body1" sx={{ mt: 1, color: '#000453' }}>
                            To start a whiteboard session, select your handout of choice and then toggle on whiteboard.
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#000453' }}>
                            Annotate our illustration and then click save. Once saved your whiteboard will appear here.
                        </Typography>
                        {/* Button aligned to the right, text unchanged */}
                        <Box
                            sx={{
                                mt: 3,
                                mb: 3,
                                display: 'flex',
                                justifyContent: { xs: 'center', sm: 'flex-end' },
                            }}
                        >
                            <PinkGradientButtonWithArrow label="Search Handouts" onClick={() => navigate('/search')} />
                        </Box>
                    </Box>
                ) : (
                    <Grid
                        container
                        spacing={3}
                        px={isMobile ? '48px' : '66px'}
                        sx={{ position: 'relative', ml: '0px!important', width: '100%' }}
                    >
                        {paginatedWhiteboards.map((whiteboard, index) => (
                            <WhiteboardCard
                                key={whiteboard.whiteboardId || index}
                                whiteboard={whiteboard}
                                isMobile={isMobile}
                                userDataStore={userDataStore}
                                authStore={authStore}
                                whiteboardStore={whiteboardStore}
                                showSnackbar={showSnackbar}
                                handleToggleFavourite={handleToggleFavourite}
                                handleEditWhiteboard={handleEditWhiteboard}
                                handleArchiveWhiteboard={handleArchiveWhiteboard}
                                imageMap={imageMap}
                                index={index}
                            />
                        ))}

                        {/* Pagination Controls */}
                        {whiteboards.length > 6 && (
                            <>
                                <IconButton
                                    onClick={handlePrevPage}
                                    disabled={page === 0}
                                    sx={{
                                        position: 'absolute',
                                        left: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronLeftRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>

                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={(page + 1) * whiteboardsPerPage >= filteredWhiteboards.length}
                                    sx={{
                                        position: 'absolute',
                                        right: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronRightRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>
                            </>
                        )}
                    </Grid>
                )}
            </Container>

            {/* New Feedback Section */}
            <FeedbackCTA />
        </>
    );
};

export default MyLibrary;
